import * as Sentry from '@sentry/react';

type WebRuntimeSentryInput = {
  severity?: 'error' | 'warning';
  source: string;
  message: string;
  path?: string;
  metadata?: Record<string, unknown>;
};

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const AUTO_CAPTURED_WEB_SOURCES = new Set(['window.error', 'window.unhandledrejection']);

let initialized = false;

function hasWebSentry() {
  return Boolean(SENTRY_DSN);
}

export function initWebSentry() {
  if (initialized || !hasWebSentry()) {
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    enabled: true,
    environment: import.meta.env.PROD ? 'production' : 'development',
    attachStacktrace: true,
    sendDefaultPii: false,
  });

  Sentry.setTag('surface', 'web');
  initialized = true;
}

export function syncWebSentryUser(input: { id: string; email?: string | null } | null) {
  if (!hasWebSentry()) {
    return;
  }

  if (!input) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({
    id: input.id,
    email: input.email || undefined,
  });
}

export function captureWebRuntimeSentryEvent(input: WebRuntimeSentryInput) {
  if (!hasWebSentry() || AUTO_CAPTURED_WEB_SOURCES.has(input.source)) {
    return;
  }

  Sentry.withScope((scope) => {
    scope.setLevel(input.severity === 'warning' ? 'warning' : 'error');
    scope.setTag('runtime-source', input.source);

    if (input.path) {
      scope.setTag('route-path', input.path);
    }

    if (input.metadata) {
      scope.setContext('adulting-runtime', input.metadata);
    }

    scope.setFingerprint(['adulting-runtime', input.source, input.path || 'unknown']);
    Sentry.captureMessage(input.message);
  });
}
